import { ForwardedRef, ReactNode, forwardRef, isValidElement } from 'react'

import { Link } from 'react-router-dom'
import { styled } from 'styled-components'

import Icon from 'core/components/Icon'
import Spinner from 'core/components/lib/Spinner/Spinner'
import { R } from 'core/helpers'
import { PendingEventHandler, usePendingCallback } from 'core/hooks'
import {
  error,
  onError,
  onPrimary,
  onSuccess,
  primary,
  success,
  surface,
  variables,
} from 'core/styles'

type ClickableProps = {
  className?: string
  /** The text and / or icon to display. */
  children?: ReactNode
  /** The visual style of the clickable. */
  variant:
    | 'link'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'outline'
    | 'ghost'
  /** The visual size of the clickable. Controls both font and physical size. */
  size?: 'xs' | 'sm' | 'md' | 'lg'
  /** Correlates to the HTML button `type` attribute. */
  type?: 'button' | 'reset' | 'submit'
  /** Shows a spinner after `children` while true. */
  isLoading?: boolean
  /** Disables all interactivity while true. */
  disabled?: boolean
  /** Navigates to the provided route when clicked. */
  to?: string
  /** Opens the provided url when clicked. Automatically shows an external link icon when set. */
  href?: string
  /** Calls the provided function when clicked. Return a promise to show a spinner while the promise resolves.  */
  onClick?: PendingEventHandler
  /** Accessible name; required when children are icon-only. */
  ariaLabel?: string
  /** Toggle semantics ported from IconButton: renders `aria-checked`; dims content when false. */
  checked?: boolean
}

/**
 * A component for buttons and links. Only one of `to`, `href`, or `onClick` can be provided.
 */
const Clickable = forwardRef<HTMLElement, ClickableProps>(
  (
    {
      variant,
      size = 'md',
      type = 'button',
      disabled = false,
      isLoading = false,
      onClick: _onClick,
      to,
      href,
      children,
      ariaLabel,
      checked,
      ...rest
    },
    ref,
  ) => {
    const [isPending, onClick] = usePendingCallback(_onClick)

    if (R.pipe([_onClick, to, href], R.filter(Boolean), R.length()) > 1) {
      console.warn(
        'Clickable: can only provide one of `onClick`, `to`, or `href`',
      )
    }

    const showSpinner = isPending || isLoading
    const isIconOnly = isValidElement(children) && children.type === Icon

    const content =
      isIconOnly && showSpinner ? (
        <Spinner width='24px' />
      ) : (
        <>
          {children}
          {showSpinner && <StyledSpinner width='24px' />}
        </>
      )

    const props = {
      'aria-checked': checked,
      'aria-label': ariaLabel,
      $checked: checked,
      $disabled: disabled || isPending,
      $size: size,
      $variant: variant,
      ...rest,
    }

    return !R.isNil(to) ? (
      <ClickableBase
        as={Link}
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        to={to}
        {...props}
      >
        {content}
      </ClickableBase>
    ) : !R.isNil(href) ? (
      <ClickableBase
        as='a'
        href={href}
        ref={ref as ForwardedRef<HTMLAnchorElement>}
        rel='noopener noreferrer'
        target='_blank'
        {...props}
        $external
      >
        <span>{children}</span>
        <Icon margin='0 0 0 8px' name='open_in_new' />
        {(isPending || isLoading) && <StyledSpinner width='24px' />}
      </ClickableBase>
    ) : (
      <ClickableBase
        disabled={disabled || isPending}
        onClick={onClick}
        ref={ref as ForwardedRef<HTMLButtonElement>}
        type={type}
        {...props}
      >
        {content}
      </ClickableBase>
    )
  },
)

Clickable.displayName = 'Clickable'

export default Clickable

export const ClickableBase = styled.button<{
  $checked?: boolean
  $external?: boolean
  $disabled: boolean
  $size: 'xs' | 'sm' | 'md' | 'lg'
  $variant:
    | 'link'
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'outline'
    | 'ghost'
}>`
  display: flex;
  align-items: center;
  margin: 0;
  border: none;
  cursor: pointer;
  width: auto;
  font-weight: 500;

  &:hover {
    filter: brightness(0.92);
  }

  &:active {
    filter: brightness(0.85);
  }

  ${(p) =>
    p.$size === 'xs'
      ? `
      border-radius: 6px;
      padding: 3px 8px;
      font-size: 12px;
      `
      : p.$size === 'sm'
      ? `
      border-radius: 6px;
      padding: 5px 10px;
      font-size: 12px;
      `
      : p.$size === 'lg'
      ? `
      border-radius: 8px;
      padding: 9px 14px;
      font-size: 16px;
      `
      : `
      border-radius: 8px;
      padding: 7px 12px;
      font-size: 14px;
      `}

  ${(p) =>
    p.$variant === 'link'
      ? `
      display: inline-flex;
      background-color: transparent;
      padding: 0;
      color: ${variables.colorBluePrimary};

      ${
        p.$external
          ? `
          &:hover > span:first-child  {
            text-decoration: underline;
          }
          `
          : `
          &:hover  {
            text-decoration: underline;
          }
          `
      }
      `
      : p.$variant === 'primary'
      ? `
      background-color: ${primary};
      color: ${onPrimary};
      `
      : p.$variant === 'secondary'
      ? `
      background-color: transparent;
      color: ${variables.colorBluePrimary};

      &:hover {
        background-color: ${surface};
        filter: none;
      }
      `
      : p.$variant === 'success'
      ? `
      background-color: ${success};
      color: ${onSuccess};
      `
      : p.$variant === 'error'
      ? `
      background-color: ${error};
      color: ${onError};
      `
      : p.$variant === 'outline'
      ? `
      background-color: ${p.theme.background};
      color: ${p.theme.text};
      border: 1px solid ${p.theme.border};

      &:hover {
        background-color: ${p.theme.background};
      }

      &:active {
        background-color: ${p.theme.background};
      }

      `
      : p.$variant === 'ghost'
      ? `
      background-color: transparent;
      color: ${p.theme.text};

      &:hover {
        background-color: ${p.theme.background};
      }

      &:active {
        background-color: transparent;
      }
      `
      : ''}

  ${(p) =>
    p.$disabled &&
    `
    ${p.$variant !== 'link' && `background-color: ${p.theme.background};`}
    cursor: default;
    outline: none;
    color: ${p.theme.disabledText};
    pointer-events: none;

    &:hover {
      filter: none;
    }

    &:active {
      filter: none;
    }
  `}

  ${(p) => p.$checked === false && 'opacity: 0.55;'}
`

const StyledSpinner = styled(Spinner)`
  margin-left: 8px;
`
